var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Expose, Type } from 'class-transformer';
import { IsArray, IsInt, IsOptional, IsString, IsUUID, ValidateNested } from 'class-validator';
/**
 * One Verordnung-chip datum for a single appointment row in the patient
 * Terminübersicht. Carries the Heilmittel concept label of the
 * treatment-unit that is linked to this appointment (the appointment↔plan
 * link lives on `TreatmentUnit.appointmentId`). Appointments with no linked
 * unit simply have no chip entry — no special case.
 */
export class PatientAppointmentChipDto {
    /** The materialized appointment this chip belongs to. */
    appointmentId;
    /** The Verordnung (treatment plan) the linked unit belongs to. */
    treatmentPlanId;
    code;
    abbreviation;
    name;
}
__decorate([
    IsUUID(),
    Expose(),
    __metadata("design:type", String)
], PatientAppointmentChipDto.prototype, "appointmentId", void 0);
__decorate([
    IsUUID(),
    Expose(),
    __metadata("design:type", String)
], PatientAppointmentChipDto.prototype, "treatmentPlanId", void 0);
__decorate([
    IsString(),
    Expose(),
    __metadata("design:type", String)
], PatientAppointmentChipDto.prototype, "code", void 0);
__decorate([
    IsString(),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], PatientAppointmentChipDto.prototype, "abbreviation", void 0);
__decorate([
    IsString(),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], PatientAppointmentChipDto.prototype, "name", void 0);
/**
 * One row of the "Meine Behandlungen" progress block — the X/N reading a
 * patient uses to decide whether to request more appointments. Reported per
 * (Verordnung, Heilmittel concept):
 *
 * - `prescribed` (verordnet): total units of this concept in the plan
 * - `open` (offen): units still to be delivered (PLANNED + SCHEDULED)
 * - `scheduled` (terminiert): the subset of open units that already have an
 *   appointment (SCHEDULED). `open - scheduled` is the call-to-action count.
 * - `delivered`: units already delivered. `delivered + scheduled` is what the
 *   patient reads as "gebucht".
 */
export class PatientTreatmentProgressEntryDto {
    treatmentPlanId;
    /** YYYY-MM-DD — the plan's `validFrom`, else its creation day. */
    planStartDate;
    code;
    abbreviation;
    name;
    prescribed;
    open;
    scheduled;
    delivered;
}
__decorate([
    IsUUID(),
    Expose(),
    __metadata("design:type", String)
], PatientTreatmentProgressEntryDto.prototype, "treatmentPlanId", void 0);
__decorate([
    IsString(),
    Expose(),
    __metadata("design:type", String)
], PatientTreatmentProgressEntryDto.prototype, "planStartDate", void 0);
__decorate([
    IsString(),
    Expose(),
    __metadata("design:type", String)
], PatientTreatmentProgressEntryDto.prototype, "code", void 0);
__decorate([
    IsString(),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], PatientTreatmentProgressEntryDto.prototype, "abbreviation", void 0);
__decorate([
    IsString(),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], PatientTreatmentProgressEntryDto.prototype, "name", void 0);
__decorate([
    IsInt(),
    Expose(),
    __metadata("design:type", Number)
], PatientTreatmentProgressEntryDto.prototype, "prescribed", void 0);
__decorate([
    IsInt(),
    Expose(),
    __metadata("design:type", Number)
], PatientTreatmentProgressEntryDto.prototype, "open", void 0);
__decorate([
    IsInt(),
    Expose(),
    __metadata("design:type", Number)
], PatientTreatmentProgressEntryDto.prototype, "scheduled", void 0);
__decorate([
    IsInt(),
    Expose(),
    __metadata("design:type", Number)
], PatientTreatmentProgressEntryDto.prototype, "delivered", void 0);
/**
 * BFF payload for the Terminübersicht Verordnung context. Composed from the
 * treatment-plan bounded context over the CQRS query bus — never a direct
 * cross-context read of treatment-plan tables from the appointment side.
 */
export class PatientAppointmentOverviewProgressDto {
    chips;
    plans;
}
__decorate([
    IsArray(),
    ValidateNested({ each: true }),
    Type(() => PatientAppointmentChipDto),
    Expose(),
    __metadata("design:type", Array)
], PatientAppointmentOverviewProgressDto.prototype, "chips", void 0);
__decorate([
    IsArray(),
    ValidateNested({ each: true }),
    Type(() => PatientTreatmentProgressEntryDto),
    Expose(),
    __metadata("design:type", Array)
], PatientAppointmentOverviewProgressDto.prototype, "plans", void 0);
//# sourceMappingURL=patient-appointment-overview.dto.js.map